/**
 * Altplan einlesen — SiGe-Pläne aus der Zeit vor der App.
 *
 * Bis 2025 entstanden die Pläne in der Excel-Vorlage „Muster-SiGePlan“ und
 * wurden als PDF geplottet. Dieser Leser holt so ein PDF zurück in die
 * Datenstruktur des Moduls: Plankopf, Hauptabschnitte, Arbeitsschritte mit
 * allen Fachspalten und Zeitraum, Notrufplan und Legende.
 *
 * Gelesen wird die TEXTEBENE samt Zellrahmen — keine KI, kein Raten: jede
 * Zelle landet in genau der Spalte, in der sie im Plan steht.
 *  - Spalten: an den Überschriften der Vorlage („Arbeiten bzw. Leistungen …“,
 *    „Schutzeinrichtung …“, „Arbeitsschutzvorschrift“ …) und den senkrechten
 *    Rahmen um sie herum.
 *  - Zeilen: an den waagerechten Rahmen. Aus den Textabständen allein ginge
 *    es nicht — Excel verteilt die Zeilen einer Zelle auf deren Höhe, der
 *    Abstand innerhalb einer Zelle gleicht dem zwischen zwei Zeilen.
 *  - Zeitraum: die Balken tragen nur „30. Okt“ / „23. Dez“. Das Jahr liefert
 *    das Monatsraster im Tabellenkopf: der Monat mit dem Namen, der dem
 *    Beschriftungsrand am nächsten liegt.
 *
 * Fehlen Spaltenköpfe oder Zellrahmen (Scan, fremde Vorlage), meldet der
 * Leser `erkannt: false`; die Oberfläche bietet dann das Abschreiben per KI an
 * (`altplanAusKi`), das dieselbe Ergebnisform liefert.
 *
 * Bewusst ohne pdf.js, React und Supabase: die Seite kommt als Liste von
 * Textstücken und Linien herein (altplanPdf.ts baut sie), die Regeln bleiben
 * ohne DOM testbar.
 */

import { HAUPTABSCHNITTE } from './types';
import type { LegendeEintrag, NotrufKontakt } from './types';
import { alsIsoDatum } from './zeitraum';

// ─────────────────────────────────────────────────────────────────────────────
// Eingabe
// ─────────────────────────────────────────────────────────────────────────────

/** Ein Textstück der Seite in pt, Ursprung oben links; `y` ist die Grundlinie. */
export interface AltplanText {
  x: number;
  y: number;
  breite: number;
  hoehe: number;
  text: string;
}

/** Ein dünnes Rechteck oder eine Linie der Seite (Zellrahmen), Ursprung oben links. */
export interface AltplanLinie {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

export interface AltplanSeite {
  breite: number;
  hoehe: number;
  texte: AltplanText[];
  linien: AltplanLinie[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Ergebnis
// ─────────────────────────────────────────────────────────────────────────────

/** Eine Zeile der Matrix, so wie sie im Altplan steht. */
export interface AltplanSchritt {
  /** Leer, wenn die Zeile vor der ersten Abschnittsüberschrift steht. */
  hauptabschnitt: string;
  bezeichnung: string;
  schutzmassnahmen: string[];
  vorschriften: string[];
  /** BG-BAU-Bausteine aus der Spalte „Hinweise“ („A 024“, „B 141“). */
  hinweis_codes: string[];
  /** ISO-Datum oder null. */
  zeitraum_von: string | null;
  zeitraum_bis: string | null;
  koordinierungsbedarf: string;
  zustaendig_erstellung: string;
  zustaendig_kontrolle: string;
  zustaendig_instandhaltung: string;
}

export interface AltplanKopf {
  bauherr_name: string;
  bauherr_adresse: string;
  baumassnahme_name: string;
  baumassnahme_adresse: string;
  plan_nr: string;
  /** ISO-Datum oder leer — so auch alle übrigen Datumsfelder. */
  plan_datum: string;
  plan_inhalt: string;
  sigeko_name: string;
  sigeko_mobil: string;
  bearbeitet_name: string;
  bearbeitet_datum: string;
  geprueft_name: string;
  geprueft_datum: string;
  /**
   * Die Excel-Vorlage führt „überarbeitet“ statt „geprüft“. Der Plankopf der
   * App kennt dafür keine Zeile, nur den Status `ueberarbeitet`.
   */
  ueberarbeitet_datum: string;
}

export interface AltplanNotruf {
  krankenhaus?: NotrufKontakt;
  durchgangsarzt?: NotrufKontakt;
  augenarzt?: NotrufKontakt;
  apotheke?: NotrufKontakt;
  ersthelfer: { name: string; firma?: string }[];
}

export interface AltplanErgebnis {
  erkannt: boolean;
  /** Warum nicht erkannt — nur bei `erkannt: false`. */
  grund?: string;
  /** 'pdf' = Textebene gelesen, 'ki' = von der KI abgeschrieben. */
  quelle: 'pdf' | 'ki';
  kopf: AltplanKopf;
  /** Hauptabschnitte in Planreihenfolge. */
  hauptabschnitte: string[];
  schritte: AltplanSchritt[];
  notruf: AltplanNotruf;
  legende: LegendeEintrag[];
  /** Was beim Lesen auffiel — die Vorschau zeigt es vor dem Anlegen. */
  hinweise: string[];
  /**
   * Seiten (ab 1), auf denen keine Plantabelle lesbar war, obwohl andere
   * Seiten gelesen wurden — etwa eine gescannte Folgeseite. Ihre Zeilen fehlen
   * im Ergebnis; die Vorschau bietet deshalb das Lesen per KI an.
   */
  ungeleseneSeiten: number[];
}

export const leererAltplanKopf = (): AltplanKopf => ({
  bauherr_name: '',
  bauherr_adresse: '',
  baumassnahme_name: '',
  baumassnahme_adresse: '',
  plan_nr: '',
  plan_datum: '',
  plan_inhalt: '',
  sigeko_name: '',
  sigeko_mobil: '',
  bearbeitet_name: '',
  bearbeitet_datum: '',
  geprueft_name: '',
  geprueft_datum: '',
  ueberarbeitet_datum: '',
});

const nichtErkannt = (grund: string): AltplanErgebnis => ({
  erkannt: false,
  grund,
  quelle: 'pdf',
  kopf: leererAltplanKopf(),
  hauptabschnitte: [],
  schritte: [],
  notruf: { ersthelfer: [] },
  legende: [],
  hinweise: [],
  ungeleseneSeiten: [],
});

// ─────────────────────────────────────────────────────────────────────────────
// Text-Helfer
// ─────────────────────────────────────────────────────────────────────────────

const leer = (s: string): string => s.replace(/\s+/g, ' ').trim();

/** Kalendertag als ISO-Datum; leer, wenn es den Tag nicht gibt. */
const isoDatum = (jahr: number, monat: number, tag: number): string => {
  if (!Number.isInteger(jahr) || monat < 1 || monat > 12 || tag < 1) return '';
  const tage = new Date(Date.UTC(jahr, monat, 0)).getUTCDate();
  if (tag > tage) return '';
  return `${jahr}-${String(monat).padStart(2, '0')}-${String(tag).padStart(2, '0')}`;
};

/** „30.10.2023“ / „30.10.23“ → „2023-10-30“, sonst leer. */
export const deutschesDatumAlsIso = (wert: string): string => {
  const m = /^(\d{1,2})\.(\d{1,2})\.(\d{4}|\d{2})$/.exec(leer(wert || ''));
  if (!m) return '';
  const jahr = m[3].length === 2 ? 2000 + Number(m[3]) : Number(m[3]);
  return isoDatum(jahr, Number(m[2]), Number(m[1]));
};

const MONATE: Record<string, number> = {
  jan: 1, feb: 2, mär: 3, mae: 3, mar: 3, mrz: 3, apr: 4, mai: 5, jun: 6,
  jul: 7, aug: 8, sep: 9, okt: 10, nov: 11, dez: 12,
};

/** „30. Okt“ / „6. März“ → Tag und Monat; null, wenn es keine Balkenbeschriftung ist. */
export const leseBalkenDatum = (text: string): { tag: number; monat: number } | null => {
  const m = /^(\d{1,2})\.\s*([A-Za-zÄÖÜäöü]{3,})\.?$/.exec(leer(text));
  if (!m) return null;
  const monat = MONATE[m[2].slice(0, 3).toLowerCase()];
  return monat ? { tag: Number(m[1]), monat } : null;
};

/** Zeilenende, nach dem der Satz offensichtlich weitergeht („Merkblätter des dt.“). */
const OFFENES_ENDE = /\s(des|der|die|das|dem|den|und|oder|bzw\.|für|mit|von|vom|zu|zum|zur|im|in|an|am|auf|bei|nach|über|unter|vor|sowie|eines|einer|einem|einen|gem\.|lt\.|dt\.|u\.|v\.|ggf\.)$/i;

/**
 * Umbrochene Zeilen einer Zelle zusammenführen. Excel trennt am Zeilenende
 * mit Bindestrich: „Vibrations-“ + „arbeitsschutzverordnung“ wird ein Wort,
 * „Maschinen-“ + „Lärmschutzverordnung“ behält den Strich. Endet eine Zeile
 * auf Artikel, Präposition oder Abkürzung, geht sie in der nächsten weiter.
 * Alle übrigen Zeilen bleiben eigene Einträge — in der Vorlage ist jede Zeile
 * eine Maßnahme bzw. eine Vorschrift, auch wenn sie klein beginnt.
 */
export const verbindeZeilen = (zeilen: string[]): string[] => {
  const aus: string[] = [];
  for (const roh of zeilen) {
    const z = leer(roh);
    if (!z) continue;
    const vorher = aus[aus.length - 1];
    if (vorher !== undefined && /[A-Za-zÄÖÜäöüß]-$/.test(vorher)) {
      if (/^(und|oder|bzw\.|sowie)\s/.test(z)) aus[aus.length - 1] = `${vorher} ${z}`;
      else if (/^[a-zäöüß]/.test(z)) aus[aus.length - 1] = vorher.slice(0, -1) + z;
      else aus[aus.length - 1] = vorher + z;
    } else if (vorher !== undefined && OFFENES_ENDE.test(vorher)) {
      aus[aus.length - 1] = `${vorher} ${z}`;
    } else {
      aus.push(z);
    }
  }
  return aus;
};

/** Aufzählungszeichen vor einem Listeneintrag entfernen. */
const ohneAufzaehlung = (s: string): string => leer(s.replace(/^[•·▪◦‣\-–]\s+/, ''));

/**
 * Inhalt der Spalte „Hinweise“ → Bausteine. Getrennt wird an Komma und
 * Semikolon; Platzhalter wie „n.v.“ oder „???“ tragen keine Ziffer und
 * fallen weg.
 */
export const leseHinweisCodes = (zeilen: string[]): string[] => {
  const aus: string[] = [];
  zeilen.join(';').split(/[;,]/).map(leer).forEach(c => {
    if (c && /\d/.test(c) && !aus.includes(c)) aus.push(c);
  });
  return aus;
};

/**
 * Überschrift auf den Standard-Abschnitt abbilden, wenn sie bis auf
 * Groß-/Kleinschreibung und Leerzeichen gleich ist — sonst bleibt sie als
 * projekteigener Abschnitt stehen („Baustelleneinrichtung - Abbrucharbeiten“).
 */
export const normalisiereAbschnitt = (titel: string): string => {
  const t = leer(titel);
  const schluessel = t.toLowerCase().replace(/\s+/g, '');
  const standard = (HAUPTABSCHNITTE as readonly string[]).find(h => h.toLowerCase().replace(/\s+/g, '') === schluessel);
  return standard || t;
};

/** Planinhalt der Vorlage („Sicherheits und gesundheitsschutzplan“) in die Schreibweise der App. */
const normalisierePlaninhalt = (s: string): string =>
  /sicherheits.*gesundheitsschutz/i.test(s) ? 'Sicherheits- und Gesundheitsschutzplan' : leer(s);

// ─────────────────────────────────────────────────────────────────────────────
// Zeilen bilden
// ─────────────────────────────────────────────────────────────────────────────

interface Zeile {
  x: number;
  x1: number;
  y: number;
  hoehe: number;
  text: string;
}

/**
 * Textstücke zu Zeilen zusammensetzen: gleiche Grundlinie und höchstens
 * `maxLuecke` Schrifthöhen Abstand. Weiter auseinander liegende Stücke auf
 * derselben Grundlinie bleiben getrennt — im Randbereich stehen zwei Spalten
 * nebeneinander (Krankenhaus | Durchgangsarzt).
 */
const bildeZeilen = (stuecke: AltplanText[], maxLuecke = 2): Zeile[] => {
  const nachY = [...stuecke].sort((a, b) => a.y - b.y);
  const gruppen: AltplanText[][] = [];
  for (const s of nachY) {
    const g = gruppen[gruppen.length - 1];
    if (g && Math.abs(s.y - g[0].y) <= Math.max(1, s.hoehe * 0.3)) g.push(s);
    else gruppen.push([s]);
  }
  const zeilen: Zeile[] = [];
  for (const g of gruppen) {
    g.sort((a, b) => a.x - b.x);
    let akt: Zeile | null = null;
    for (const s of g) {
      const luecke = akt ? s.x - akt.x1 : Infinity;
      if (akt && luecke <= maxLuecke * Math.max(s.hoehe, akt.hoehe)) {
        akt.text += luecke > 0.15 * s.hoehe ? ` ${s.text}` : s.text;
        akt.x1 = Math.max(akt.x1, s.x + s.breite);
        akt.hoehe = Math.max(akt.hoehe, s.hoehe);
      } else {
        akt = { x: s.x, x1: s.x + s.breite, y: s.y, hoehe: s.hoehe, text: s.text };
        zeilen.push(akt);
      }
    }
  }
  return zeilen.map(z => ({ ...z, text: leer(z.text) })).filter(z => z.text);
};

/** Senkrechte Mitte der Schrift — die Grundlinie liegt unter ihr. */
const mitteY = (t: { y: number; hoehe: number }): number => t.y - 0.35 * t.hoehe;

/** Werte, die dichter als `toleranz` beieinander liegen, zu einem zusammenfassen. */
const buendle = (werte: number[], toleranz = 1.5): number[] => {
  const aus: number[] = [];
  [...werte].sort((a, b) => a - b).forEach(w => {
    if (aus.length && w - aus[aus.length - 1] <= toleranz) return;
    aus.push(w);
  });
  return aus;
};

// ─────────────────────────────────────────────────────────────────────────────
// Spalten
// ─────────────────────────────────────────────────────────────────────────────

type SpaltenKey = 'arbeiten' | 'massnahme' | 'vorschrift' | 'hinweis' | 'zeit' | 'koord' | 'erst' | 'kontr' | 'inst';

/** Reihenfolge = Reihenfolge der Spalten in der Vorlage. */
const KOPF_MUSTER: { key: SpaltenKey; muster: RegExp }[] = [
  { key: 'arbeiten', muster: /^arbeiten bzw\.? leistungen/ },
  { key: 'massnahme', muster: /^schutzeinrichtung/ },
  { key: 'vorschrift', muster: /^arbeitsschutzvorschrift/ },
  { key: 'hinweis', muster: /^hinweise?$/ },
  { key: 'zeit', muster: /^zeitraum/ },
  { key: 'koord', muster: /^koordinierungsbedarf/ },
  { key: 'erst', muster: /^erstellung$/ },
  { key: 'kontr', muster: /^kontrolle$/ },
  { key: 'inst', muster: /^instandhaltung$/ },
];

interface Spalte {
  key: SpaltenKey;
  links: number;
  rechts: number;
}

interface Spalten {
  liste: Spalte[];
  nach: Partial<Record<SpaltenKey, Spalte>>;
  /** Grundlinie der Überschrift „Arbeiten bzw. Leistungen …“. */
  kopfY: number;
}

/**
 * Spaltengrenzen aus den Überschriften der Vorlage. Je Überschrift gelten die
 * nächsten senkrechten Rahmen links und rechts, die auf ihrer Höhe liegen.
 * Liegen mehrere Überschriften zwischen denselben Rahmen (Erstellung |
 * Kontrolle | Instandhaltung ist in der Vorlage ohne Trennlinien gezeichnet),
 * teilen sie sich die Breite zu gleichen Teilen. Die Zeitachse liegt zwischen
 * ihren Nachbarn — innerhalb von ihr stehen die Monatslinien.
 */
const findeSpalten = (seite: AltplanSeite, zeilen: Zeile[]): Spalten | null => {
  const labels = new Map<SpaltenKey, Zeile>();
  for (const { key, muster } of KOPF_MUSTER) {
    const treffer = zeilen
      .filter(z => muster.test(z.text.toLowerCase()))
      .sort((a, b) => a.y - b.y)[0];
    if (treffer) labels.set(key, treffer);
  }
  if (!labels.has('arbeiten') || !labels.has('massnahme') || !labels.has('vorschrift')) return null;

  const senkrecht = seite.linien
    .filter(l => l.x1 - l.x0 <= 3 && l.y1 - l.y0 >= 6)
    .map(l => ({ x: (l.x0 + l.x1) / 2, y0: l.y0, y1: l.y1 }));

  const vorhanden = KOPF_MUSTER.map(k => k.key).filter(k => labels.has(k));
  const grenzen = new Map<SpaltenKey, { links: number; rechts: number }>();

  // Erst alle Spalten außer der Zeitachse.
  const gruppen = new Map<string, SpaltenKey[]>();
  vorhanden.forEach((key, i) => {
    if (key === 'zeit') return;
    const l = labels.get(key)!;
    const mx = (l.x + l.x1) / 2;
    const my = mitteY(l);
    const xs = buendle(senkrecht.filter(s => s.y0 - 2 <= my && s.y1 + 2 >= my).map(s => s.x));
    let links = xs.filter(x => x <= mx).pop();
    let rechts = xs.find(x => x > mx);
    // Ohne Rahmen: Mitte zwischen den Nachbarüberschriften.
    if (links === undefined) {
      const vor = labels.get(vorhanden[i - 1]);
      links = vor ? (vor.x1 + l.x) / 2 : 0;
    }
    if (rechts === undefined) {
      const nach = labels.get(vorhanden[i + 1]);
      rechts = nach ? (l.x1 + nach.x) / 2 : seite.breite;
    }
    const g = `${links.toFixed(1)}|${rechts.toFixed(1)}`;
    gruppen.set(g, [...(gruppen.get(g) || []), key]);
    grenzen.set(key, { links, rechts });
  });
  gruppen.forEach(keys => {
    if (keys.length < 2) return;
    const { links, rechts } = grenzen.get(keys[0])!;
    const breite = (rechts - links) / keys.length;
    keys
      .sort((a, b) => labels.get(a)!.x - labels.get(b)!.x)
      .forEach((k, i) => grenzen.set(k, { links: links + i * breite, rechts: links + (i + 1) * breite }));
  });

  if (labels.has('zeit')) {
    const idx = vorhanden.indexOf('zeit');
    const vor = vorhanden.slice(0, idx).reverse().find(k => grenzen.has(k));
    const nach = vorhanden.slice(idx + 1).find(k => grenzen.has(k));
    const l = labels.get('zeit')!;
    grenzen.set('zeit', {
      links: vor ? grenzen.get(vor)!.rechts : l.x,
      rechts: nach ? grenzen.get(nach)!.links : l.x1,
    });
  }

  const liste = vorhanden.map(key => ({ key, ...grenzen.get(key)! }));
  const nach: Partial<Record<SpaltenKey, Spalte>> = {};
  liste.forEach(s => { nach[s.key] = s; });
  return { liste, nach, kopfY: labels.get('arbeiten')!.y };
};

/** Spalte eines Textstücks: Inhalte stehen linksbündig, deshalb zählt der linke Rand. */
const spalteVon = (spalten: Spalte[], x: number): Spalte | undefined =>
  spalten.find(s => x >= s.links - 1 && x < s.rechts - 1);

/** Ungefähre Zeichenbreite einer Groteskschrift in 1/1000 em — genug, um eine Wortgrenze zu treffen. */
const zeichenBreite = (c: string): number => {
  if (" .,:;!|'ijlI".includes(c)) return 260;
  if ('ftr/()[]-'.includes(c)) return 320;
  if ('mwMW'.includes(c)) return 860;
  if (/[A-ZÄÖÜ]/.test(c)) return 680;
  return 540;
};

/**
 * Manche PDF-Erzeuger legen zwei Nachbarzellen derselben Zeile in EIN
 * Textstück („Arbeiten an Wandöffnungen innen: Absturz 3-teil. Seitenschutz
 * …“). Reicht ein Stück über den rechten Rand seiner Spalte, wird es an dem
 * Leerzeichen geteilt, das dem Rand am nächsten liegt; die Lage der Zeichen
 * wird aus ihrer ungefähren Breite geschätzt. Liegt kein Leerzeichen nahe
 * genug, bleibt das Stück ganz.
 */
const teileAnSpalten = (t: AltplanText, spalten: Spalte[]): AltplanText[] => {
  const start = spalteVon(spalten, t.x);
  if (!start || t.x + t.breite <= start.rechts + 1) return [t];
  const zeichen = [...t.text];
  const breiten = zeichen.map(zeichenBreite);
  const faktor = t.breite / breiten.reduce((a, b) => a + b, 0);
  let pos = t.x;
  let bester = -1;
  let besterAbstand = Infinity;
  zeichen.forEach((c, i) => {
    pos += breiten[i] * faktor;
    if (c !== ' ') return;
    const d = Math.abs(pos - start.rechts);
    if (d < besterAbstand) { besterAbstand = d; bester = i; }
  });
  if (bester <= 0 || bester >= zeichen.length - 1 || besterAbstand > 3 * t.hoehe) return [t];
  const links: AltplanText = { ...t, breite: start.rechts - 1 - t.x, text: zeichen.slice(0, bester).join('') };
  const rechts: AltplanText = { ...t, x: start.rechts + 1, breite: t.x + t.breite - start.rechts - 1, text: zeichen.slice(bester + 1).join('') };
  return [links, ...teileAnSpalten(rechts, spalten)];
};

// ─────────────────────────────────────────────────────────────────────────────
// Zeitachse
// ─────────────────────────────────────────────────────────────────────────────

interface MonatsZelle {
  jahr: number;
  monat: number;
  x: number;
}

/**
 * Monatsraster aus dem Tabellenkopf: die Monatszahlen von links nach rechts,
 * ein Jahreswechsel dort, wo die Zahl wieder kleiner wird. Verankert wird an
 * der Jahreszahl, die über einem Lauf steht. Ohne Monatszahlen oder ohne
 * eine einzige lesbare Jahreszahl gibt es kein Raster.
 */
const baueMonatsraster = (kopf: AltplanText[]): MonatsZelle[] | null => {
  const zahlen = kopf.filter(t => /^\d{1,2}$/.test(leer(t.text)) && Number(t.text) >= 1 && Number(t.text) <= 12);
  // Nur die Zeile mit den meisten Zahlen ist die Monatszeile — eine am Rand
  // abgeschnittene Jahreszahl („2“ statt „2027“) stünde sonst als Februar da.
  const jeZeile = new Map<number, AltplanText[]>();
  zahlen.forEach(t => {
    const y = Math.round(t.y);
    const schluessel = [...jeZeile.keys()].find(k => Math.abs(k - y) <= 1.5) ?? y;
    jeZeile.set(schluessel, [...(jeZeile.get(schluessel) || []), t]);
  });
  const monatsZeile = [...jeZeile.values()].sort((a, b) => b.length - a.length)[0] || [];
  const monate = monatsZeile
    .map(t => ({ monat: Number(t.text), x: t.x + t.breite / 2 }))
    .sort((a, b) => a.x - b.x);
  const jahre = kopf
    .filter(t => /^(19|20)\d{2}$/.test(leer(t.text)))
    .map(t => ({ jahr: Number(t.text), x: t.x + t.breite / 2 }));
  if (monate.length < 2 || jahre.length === 0) return null;

  const laeufe: { monat: number; x: number }[][] = [];
  monate.forEach(m => {
    const lauf = laeufe[laeufe.length - 1];
    if (lauf && m.monat > lauf[lauf.length - 1].monat) lauf.push(m);
    else laeufe.push([m]);
  });
  const zelle = (monate[monate.length - 1].x - monate[0].x) / Math.max(1, monate.length - 1);
  const abstand = (lauf: { x: number }[], x: number): number => {
    const von = lauf[0].x - zelle / 2;
    const bis = lauf[lauf.length - 1].x + zelle / 2;
    return x < von ? von - x : x > bis ? x - bis : 0;
  };
  // Jede Jahreszahl gehört zum nächstgelegenen Lauf; die erste verankert.
  const anker = jahre
    .map(j => {
      let bester = 0;
      laeufe.forEach((lauf, i) => { if (abstand(lauf, j.x) < abstand(laeufe[bester], j.x)) bester = i; });
      return { lauf: bester, jahr: j.jahr, d: abstand(laeufe[bester], j.x) };
    })
    .sort((a, b) => a.d - b.d)[0];
  const zellen: MonatsZelle[] = [];
  laeufe.forEach((lauf, i) => {
    const jahr = anker.jahr + (i - anker.lauf);
    lauf.forEach(m => zellen.push({ jahr, monat: m.monat, x: m.x }));
  });
  return zellen;
};

/** Balkenbeschriftung → Datum: der gleichnamige Monat, der `bezugX` am nächsten liegt. */
const datumAmRaster = (raster: MonatsZelle[], tag: number, monat: number, bezugX: number): string => {
  const bester = raster
    .filter(z => z.monat === monat)
    .sort((a, b) => Math.abs(a.x - bezugX) - Math.abs(b.x - bezugX))[0];
  return bester ? isoDatum(bester.jahr, monat, tag) : '';
};

// ─────────────────────────────────────────────────────────────────────────────
// Randbereich: Plankopf, Notrufplan, Legende
// ─────────────────────────────────────────────────────────────────────────────

const LABEL_MUSTER = {
  bauherr: /^bauherr(in)?:?$/i,
  baumassnahme: /^(bauma(ß|ss)nahme|bauvorhaben):?$/i,
  planinhalt: /^planinhalt:?$/i,
  planNr: /^plan[\s-]*nr\.?:?$/i,
  datum: /^datum:?$/i,
  bearbeitet: /^bearbeitet:?$/i,
  ueberarbeitet: /^(über|ueber)arbeitet:?$/i,
  geprueft: /^gepr(ü|ue)ft:?$/i,
  krankenhaus: /^(nächstes\s+)?krankenhaus:?$/i,
  durchgangsarzt: /^(durchgangsarzt|d-arzt):?$/i,
  augenarzt: /^augenarzt:?$/i,
  apotheke: /^apotheke:?$/i,
  ersthelfer: /^ersthelfer/i,
  legende: /^legende:?$/i,
};

const istLabel = (z: Zeile): boolean => Object.values(LABEL_MUSTER).some(m => m.test(z.text));

/**
 * Zeilen unter einer Überschrift in derselben Spalte — bis zur nächsten
 * Überschrift oder bis zu einer Lücke von mehr als drei Schrifthöhen.
 */
const darunter = (zeilen: Zeile[], label: Zeile, dx = 12): Zeile[] => {
  const spalte = zeilen
    .filter(z => z !== label && Math.abs(z.x - label.x) <= dx && z.y > label.y + 0.5)
    .sort((a, b) => a.y - b.y);
  const aus: Zeile[] = [];
  let letzte = label;
  for (const z of spalte) {
    if (istLabel(z)) break;
    if (z.y - letzte.y > 3 * Math.max(z.hoehe, letzte.hoehe)) break;
    aus.push(z);
    letzte = z;
  }
  return aus;
};

/** Werte rechts neben einem Label auf derselben Grundlinie, von links nach rechts. */
const rechtsDaneben = (zeilen: Zeile[], label: Zeile): Zeile[] =>
  zeilen
    .filter(z => z !== label && Math.abs(z.y - label.y) <= Math.max(1.5, label.hoehe * 0.3) && z.x > label.x1 - 0.5)
    .sort((a, b) => a.x - b.x);

const PLZ = /^\d{5}\b/;
const TELEFON = /^(tel(efon)?\.?|fon|☎)\s*:?\s*/i;

/** Namenszeilen verbinden: Trennstrich am Ende hängt an, sonst Komma. */
const verbindeName = (zeilen: string[]): string =>
  zeilen.reduce((acc, z) => (!acc ? z : /[A-Za-zÄÖÜäöüß]-$/.test(acc) ? acc + z : `${acc}, ${z}`), '');

/**
 * Kontaktblock des Notrufplans: Namenszeilen bis zur ersten Anschriftzeile
 * (Straße mit Hausnummer oder Postleitzahl), Telefon an „Tel.“ erkannt.
 */
const leseKontakt = (zeilen: Zeile[]): NotrufKontakt | undefined => {
  const texte = zeilen.map(z => z.text);
  const telefon = texte.find(t => TELEFON.test(t));
  const rest = texte.filter(t => t !== telefon);
  const ab = rest.findIndex(t => PLZ.test(t) || (/\d/.test(t) && !/^dr\.?\s/i.test(t)));
  const name = verbindeName(verbindeZeilen(ab === -1 ? rest : rest.slice(0, ab)));
  const adresse = ab === -1 ? '' : rest.slice(ab).join(', ');
  if (!name && !adresse && !telefon) return undefined;
  const k: NotrufKontakt = {};
  if (name) k.name = name;
  if (adresse) k.adresse = adresse;
  if (telefon) k.telefon = leer(telefon.replace(TELEFON, ''));
  return k;
};

/** Bauherr / Baumaßnahme: die große Schrift ist der Name, die kleine die Anschrift. */
const leseNameAdresse = (zeilen: Zeile[]): { name: string; adresse: string } => {
  if (!zeilen.length) return { name: '', adresse: '' };
  const gross = Math.max(...zeilen.map(z => z.hoehe));
  const klein = Math.min(...zeilen.map(z => z.hoehe));
  const istName = (z: Zeile, i: number) => (gross - klein > 1 ? z.hoehe >= gross - 0.5 : i === 0);
  const name = verbindeZeilen(zeilen.filter(istName).map(z => z.text)).join(' ');
  const adresse = zeilen
    .filter((z, i) => !istName(z, i))
    .map(z => z.text.replace(/\s*[•·]\s*/g, ', '))
    .join(', ');
  return { name, adresse };
};

/** Datum und Name rechts neben „bearbeitet“ / „geprüft“ / „überarbeitet“. */
const leseStempelZeile = (zeilen: Zeile[], label: Zeile | undefined): { datum: string; name: string } => {
  if (!label) return { datum: '', name: '' };
  const werte = rechtsDaneben(zeilen, label);
  const datumZeile = werte.find(w => deutschesDatumAlsIso(w.text));
  const name = werte.filter(w => w !== datumZeile && !istLabel(w)).map(w => w.text).join(' ');
  return { datum: datumZeile ? deutschesDatumAlsIso(datumZeile.text) : '', name };
};

const leseRand = (zeilen: Zeile[], roh: AltplanText[]): { kopf: AltplanKopf; notruf: AltplanNotruf; legende: LegendeEintrag[] } => {
  const kopf = leererAltplanKopf();
  const erstes = (m: RegExp) => zeilen.filter(z => m.test(z.text)).sort((a, b) => a.y - b.y || a.x - b.x)[0];

  const bauherr = erstes(LABEL_MUSTER.bauherr);
  if (bauherr) {
    const { name, adresse } = leseNameAdresse(darunter(zeilen, bauherr));
    kopf.bauherr_name = name;
    kopf.bauherr_adresse = adresse;
  }
  const massnahme = erstes(LABEL_MUSTER.baumassnahme);
  if (massnahme) {
    const { name, adresse } = leseNameAdresse(darunter(zeilen, massnahme));
    kopf.baumassnahme_name = name;
    kopf.baumassnahme_adresse = adresse;
  }
  const inhalt = erstes(LABEL_MUSTER.planinhalt);
  if (inhalt) kopf.plan_inhalt = normalisierePlaninhalt(darunter(zeilen, inhalt).map(z => z.text).join(' '));

  const planNr = erstes(LABEL_MUSTER.planNr);
  if (planNr) kopf.plan_nr = rechtsDaneben(zeilen, planNr)[0]?.text || '';
  // „Datum“ steht zweimal im Stempel: als Spaltenkopf über „bearbeitet“ und
  // neben der Plan-Nr. Gemeint ist das mit einem Datum daneben.
  const datum = zeilen
    .filter(z => LABEL_MUSTER.datum.test(z.text))
    .map(z => deutschesDatumAlsIso(rechtsDaneben(zeilen, z)[0]?.text || ''))
    .find(Boolean);
  if (datum) kopf.plan_datum = datum;

  const bearbeitet = leseStempelZeile(zeilen, erstes(LABEL_MUSTER.bearbeitet));
  kopf.bearbeitet_datum = bearbeitet.datum;
  kopf.bearbeitet_name = bearbeitet.name;
  const geprueft = leseStempelZeile(zeilen, erstes(LABEL_MUSTER.geprueft));
  kopf.geprueft_datum = geprueft.datum;
  kopf.geprueft_name = geprueft.name;
  kopf.ueberarbeitet_datum = leseStempelZeile(zeilen, erstes(LABEL_MUSTER.ueberarbeitet)).datum;

  const sigeko = zeilen.map(z => /^sigeko\s*:\s*(.+)$/i.exec(z.text)?.[1]).find(Boolean);
  if (sigeko) kopf.sigeko_name = leer(sigeko);
  const mobil = zeilen.map(z => /^mobil\s*:\s*(.+)$/i.exec(z.text)?.[1]).find(Boolean);
  if (mobil) kopf.sigeko_mobil = leer(mobil);

  const notruf: AltplanNotruf = { ersthelfer: [] };
  const kontakt = (m: RegExp) => {
    const label = erstes(m);
    return label ? leseKontakt(darunter(zeilen, label)) : undefined;
  };
  notruf.krankenhaus = kontakt(LABEL_MUSTER.krankenhaus);
  notruf.durchgangsarzt = kontakt(LABEL_MUSTER.durchgangsarzt);
  notruf.augenarzt = kontakt(LABEL_MUSTER.augenarzt);
  notruf.apotheke = kontakt(LABEL_MUSTER.apotheke);

  // Ersthelfer: Tabelle „Name | Firma“; in den meisten Altplänen leer.
  const ersthelfer = erstes(LABEL_MUSTER.ersthelfer);
  if (ersthelfer) {
    const kopfName = zeilen.find(z => /^name$/i.test(z.text) && z.y > ersthelfer.y && z.y - ersthelfer.y < 4 * ersthelfer.hoehe);
    const kopfFirma = kopfName && rechtsDaneben(zeilen, kopfName).find(z => /^firma$/i.test(z.text));
    if (kopfName) {
      darunter(zeilen, kopfName).forEach(z => {
        const firma = kopfFirma
          ? rechtsDaneben(zeilen, z).find(w => Math.abs(w.x - kopfFirma.x) <= 12)?.text
          : undefined;
        notruf.ersthelfer.push(firma ? { name: z.text, firma } : { name: z.text });
      });
    }
  }

  // Legende: Kürzel und Bedeutung stehen so dicht, dass sie beim Bilden der
  // Zeilen teils verschmelzen („BaustellenO Baustellenordnung“). Getrennt wird
  // deshalb an den Rohstücken, an der Spalte, in der die Bedeutungen beginnen.
  const legende: LegendeEintrag[] = [];
  const legendeLabel = erstes(LABEL_MUSTER.legende);
  if (legendeLabel) {
    const stuecke = darunter(zeilen, legendeLabel).map(z =>
      roh.filter(t => Math.abs(t.y - z.y) <= 1.5 && t.x >= z.x - 1).sort((a, b) => a.x - b.x));
    const zweite = stuecke.filter(s => s.length > 1).map(s => s[1].x).sort((a, b) => a - b);
    const spaltenX = zweite[Math.floor(zweite.length / 2)];
    if (spaltenX !== undefined) {
      stuecke.forEach(s => {
        const kuerzel = leer(s.filter(t => t.x < spaltenX - 2).map(t => t.text).join(' '));
        const bedeutung = leer(s.filter(t => t.x >= spaltenX - 2).map(t => t.text).join(' '));
        if (kuerzel && bedeutung) legende.push({ kuerzel, bedeutung });
      });
    }
  }
  return { kopf, notruf, legende };
};

// ─────────────────────────────────────────────────────────────────────────────
// Seite lesen
// ─────────────────────────────────────────────────────────────────────────────

interface SeitenStand {
  abschnitt: string;
  spalten: Spalten | null;
}

interface SeitenErgebnis {
  schritte: AltplanSchritt[];
  abschnitte: string[];
  rand: Zeile[];
  randStuecke: AltplanText[];
  hinweise: string[];
  ohneRaster: boolean;
}

const leseSeite = (seite: AltplanSeite, stand: SeitenStand): SeitenErgebnis | null => {
  // Eng zusammensetzen: die Spaltenköpfe stehen auf derselben Grundlinie
  // nebeneinander und dürfen nicht zu einer Zeile verschmelzen.
  const alleZeilen = bildeZeilen(seite.texte, 1);
  const spalten = findeSpalten(seite, alleZeilen) || stand.spalten;
  if (!spalten) return null;
  stand.spalten = spalten;

  const arbeiten = spalten.nach.arbeiten!;
  const tabelleRechts = Math.max(...spalten.liste.map(s => s.rechts));

  // Waagerechte Rahmen, die über die ganze erste Spalte reichen: das sind die
  // Zeilengrenzen. Kürzere Striche (Schraffur der Balken) zählen nicht.
  const grenzen = buendle(
    seite.linien
      .filter(l => l.y1 - l.y0 <= 3 && l.x0 <= arbeiten.links + 4 && l.x1 >= arbeiten.rechts - 4)
      .map(l => (l.y0 + l.y1) / 2)
      .filter(y => y > spalten.kopfY),
  );
  if (grenzen.length < 2) return null;
  const koerperOben = grenzen[0];
  const koerperUnten = grenzen[grenzen.length - 1];

  const inTabelle = seite.texte.filter(t => t.x >= arbeiten.links - 1 && t.x < tabelleRechts - 1);
  const zeit = spalten.nach.zeit;
  const raster = zeit
    ? baueMonatsraster(inTabelle.filter(t => t.y < koerperOben && t.x >= zeit.links - 1 && t.x < zeit.rechts + 1))
    : null;

  const roherKoerper = inTabelle.filter(t => mitteY(t) > koerperOben && mitteY(t) < koerperUnten);
  const hoehen = roherKoerper.filter(t => spalteVon(spalten.liste, t.x)?.key === 'arbeiten').map(t => t.hoehe).sort((a, b) => a - b);
  const grundHoehe = hoehen[Math.floor(hoehen.length / 2)] || 0;

  const schritte: AltplanSchritt[] = [];
  const abschnitte: string[] = [];
  const hinweise: string[] = [];

  // Überschriften (große Schrift) laufen absichtlich über alle Spalten und
  // bleiben ganz; alle übrigen Stücke werden an Spaltengrenzen geteilt.
  const koerper = roherKoerper.flatMap(t => {
    if (grundHoehe > 0 && t.hoehe >= grundHoehe * 1.25) return [t];
    const teile = teileAnSpalten(t, spalten.liste);
    if (teile.length > 1) {
      hinweise.push(`„${leer(t.text)}“ stand im PDF über ${teile.length} Spalten und wurde geteilt — bitte prüfen.`);
    }
    return teile;
  });

  for (let i = 0; i + 1 < grenzen.length; i++) {
    const oben = grenzen[i];
    const unten = grenzen[i + 1];
    const band = koerper.filter(t => mitteY(t) > oben && mitteY(t) < unten);
    if (!band.length) continue;

    // Abschnittsüberschrift: größere Schrift in der ersten Spalte.
    const ueberschrift = grundHoehe > 0 && band.some(t => t.hoehe >= grundHoehe * 1.25 && spalteVon(spalten.liste, t.x)?.key === 'arbeiten');
    if (ueberschrift) {
      const titel = normalisiereAbschnitt(bildeZeilen(band, 4).map(z => z.text).join(' '));
      if (titel) {
        stand.abschnitt = titel;
        abschnitte.push(titel);
      }
      continue;
    }

    const zellen = (key: SpaltenKey): string[] =>
      bildeZeilen(band.filter(t => spalteVon(spalten.liste, t.x)?.key === key)).map(z => z.text);

    const bezeichnung = verbindeZeilen(zellen('arbeiten')).join(' ');

    let von = '';
    let bis = '';
    if (zeit) {
      const marken = band
        .filter(t => spalteVon(spalten.liste, t.x)?.key === 'zeit')
        .map(t => ({ t, d: leseBalkenDatum(t.text) }))
        .filter((m): m is { t: AltplanText; d: { tag: number; monat: number } } => !!m.d)
        .sort((a, b) => a.t.x - b.t.x);
      if (marken.length && raster) {
        const start = marken[0];
        const ende = marken[marken.length - 1];
        von = datumAmRaster(raster, start.d.tag, start.d.monat, start.t.x);
        bis = marken.length > 1 ? datumAmRaster(raster, ende.d.tag, ende.d.monat, ende.t.x + ende.t.breite) : '';
        if (marken.length === 1) hinweise.push(`„${bezeichnung || 'Zeile ohne Bezeichnung'}“: nur ein Datum am Zeitbalken lesbar — Ende fehlt.`);
        if (von && bis && von > bis) hinweise.push(`„${bezeichnung}“: Zeitraum ${von} bis ${bis} unplausibel — bitte prüfen.`);
      }
    }

    const schritt: AltplanSchritt = {
      hauptabschnitt: stand.abschnitt,
      bezeichnung,
      schutzmassnahmen: verbindeZeilen(zellen('massnahme')).map(ohneAufzaehlung).filter(Boolean),
      vorschriften: verbindeZeilen(zellen('vorschrift')).map(ohneAufzaehlung).filter(Boolean),
      hinweis_codes: leseHinweisCodes(zellen('hinweis')),
      zeitraum_von: von || null,
      zeitraum_bis: bis || null,
      koordinierungsbedarf: verbindeZeilen(zellen('koord')).join(' '),
      zustaendig_erstellung: verbindeZeilen(zellen('erst')).join(' '),
      zustaendig_kontrolle: verbindeZeilen(zellen('kontr')).join(' '),
      zustaendig_instandhaltung: verbindeZeilen(zellen('inst')).join(' '),
    };
    const hatInhalt = schritt.bezeichnung || schritt.schutzmassnahmen.length || schritt.vorschriften.length;
    if (!hatInhalt) continue;
    if (!schritt.bezeichnung) {
      schritt.bezeichnung = '(ohne Bezeichnung)';
      hinweise.push(`Eine Zeile unter „${stand.abschnitt || 'ohne Abschnitt'}“ hat keine Bezeichnung — bitte im Editor ergänzen.`);
    }
    schritte.push(schritt);
  }

  // Im Rand stehen Kürzel und Bedeutung der Legende nur eine Handbreit
  // auseinander („BaustellenO  Baustellenordnung“) — deshalb eng.
  const randStuecke = seite.texte.filter(t => t.x >= tabelleRechts - 1);
  const rand = bildeZeilen(randStuecke, 0.8);
  return { schritte, abschnitte, rand, randStuecke, hinweise, ohneRaster: !!zeit && !raster };
};

/** Leere Felder des Kopfes aus einer späteren Seite auffüllen. */
const ergaenzeKopf = (ziel: AltplanKopf, quelle: AltplanKopf) => {
  (Object.keys(ziel) as (keyof AltplanKopf)[]).forEach(k => { if (!ziel[k] && quelle[k]) ziel[k] = quelle[k]; });
};

/** Der eigentliche Leser — siehe Dateikopf. */
export const leseAltplan = (seiten: AltplanSeite[]): AltplanErgebnis => {
  if (!seiten.length) return nichtErkannt('Die Datei enthält keine Seite.');
  if (seiten.every(s => s.texte.length === 0)) {
    return nichtErkannt('Das PDF enthält keinen Text — vermutlich ein Scan.');
  }

  const stand: SeitenStand = { abschnitt: '', spalten: null };
  const kopf = leererAltplanKopf();
  const notruf: AltplanNotruf = { ersthelfer: [] };
  const legende: LegendeEintrag[] = [];
  const schritte: AltplanSchritt[] = [];
  const hauptabschnitte: string[] = [];
  const hinweise: string[] = [];
  let ohneRaster = false;
  let gelesen = 0;
  const ungeleseneSeiten: number[] = [];

  seiten.forEach((seite, i) => {
    const e = leseSeite(seite, stand);
    if (!e) {
      ungeleseneSeiten.push(i + 1);
      return;
    }
    gelesen++;
    schritte.push(...e.schritte);
    e.abschnitte.forEach(a => { if (!hauptabschnitte.includes(a)) hauptabschnitte.push(a); });
    hinweise.push(...e.hinweise);
    ohneRaster = ohneRaster || e.ohneRaster;
    const rand = leseRand(e.rand, e.randStuecke);
    ergaenzeKopf(kopf, rand.kopf);
    (['krankenhaus', 'durchgangsarzt', 'augenarzt', 'apotheke'] as const).forEach(k => {
      if (!notruf[k] && rand.notruf[k]) notruf[k] = rand.notruf[k];
    });
    if (!notruf.ersthelfer.length) notruf.ersthelfer = rand.notruf.ersthelfer;
    if (!legende.length) legende.push(...rand.legende);
  });

  if (!gelesen) {
    return nichtErkannt('Die Spaltenköpfe der SiGe-Plan-Vorlage („Arbeiten bzw. Leistungen“, „Schutzeinrichtung“, „Arbeitsschutzvorschrift“) samt Zellrahmen wurden nicht gefunden.');
  }
  if (!schritte.length) return nichtErkannt('Die Tabelle wurde gefunden, enthält aber keine lesbare Zeile.');

  if (ohneRaster) hinweise.push('Die Zeitachse hat kein lesbares Monatsraster — die Zeiträume bleiben leer.');
  const ohneZeit = schritte.filter(s => !s.zeitraum_von).length;
  if (ohneZeit && !ohneRaster) hinweise.push(`${ohneZeit} Arbeitsschritt(e) ohne lesbaren Zeitbalken — Zeitraum im Editor ergänzen.`);
  if (!kopf.plan_nr) hinweise.push('Keine Plan-Nr. im Plankopf gefunden.');
  if (!kopf.plan_datum) hinweise.push('Kein Plandatum im Plankopf gefunden.');

  // Seiten ohne lesbare Tabelle fallen nicht still weg (`ungeleseneSeiten`):
  // war es ein Scan der Fortsetzung, fehlten deren Zeilen, ohne dass es
  // jemand merkt. Die Vorschau meldet sie und bietet die KI an.
  return { erkannt: true, quelle: 'pdf', kopf, hauptabschnitte, schritte, notruf, legende, hinweise, ungeleseneSeiten };
};

// ─────────────────────────────────────────────────────────────────────────────
// KI-Rückfall
// ─────────────────────────────────────────────────────────────────────────────

const text = (v: unknown): string => (typeof v === 'string' ? leer(v) : typeof v === 'number' ? String(v) : '');
const textListe = (v: unknown): string[] => (Array.isArray(v) ? v.map(text).filter(Boolean) : []);
const objekt = (v: unknown): Record<string, unknown> => (v && typeof v === 'object' && !Array.isArray(v) ? v as Record<string, unknown> : {});
/** ISO oder deutsches Datum der KI → ISO, sonst leer. */
const kiDatum = (v: unknown): string => alsIsoDatum(v) || deutschesDatumAlsIso(text(v));

const kiKontakt = (v: unknown): NotrufKontakt | undefined => {
  const o = objekt(v);
  const k: NotrufKontakt = {};
  if (text(o.name)) k.name = text(o.name);
  if (text(o.adresse)) k.adresse = text(o.adresse);
  if (text(o.telefon)) k.telefon = text(o.telefon);
  return Object.keys(k).length ? k : undefined;
};

/**
 * Antwort der KI (Prompt `AI_PROMPTS.ALTPLAN_ABSCHRIFT`) → dieselbe
 * Ergebnisform wie der PDF-Leser. Was nicht passt, fällt still weg; Daten
 * nur als echte Kalendertage.
 */
export const altplanAusKi = (antwort: unknown): AltplanErgebnis => {
  const o = objekt(antwort);
  const pk = objekt(o.plankopf);
  const kopf = leererAltplanKopf();
  (['bauherr_name', 'bauherr_adresse', 'baumassnahme_name', 'baumassnahme_adresse', 'plan_nr', 'sigeko_name',
    'sigeko_mobil', 'bearbeitet_name', 'geprueft_name'] as const).forEach(k => { kopf[k] = text(pk[k]); });
  kopf.plan_inhalt = normalisierePlaninhalt(text(pk.plan_inhalt));
  kopf.plan_datum = kiDatum(pk.plan_datum);
  kopf.bearbeitet_datum = kiDatum(pk.bearbeitet_datum);
  kopf.geprueft_datum = kiDatum(pk.geprueft_datum);
  kopf.ueberarbeitet_datum = kiDatum(pk.ueberarbeitet_datum);

  const hauptabschnitte: string[] = [];
  const schritte: AltplanSchritt[] = [];
  (Array.isArray(o.abschnitte) ? o.abschnitte : []).forEach(a => {
    const ab = objekt(a);
    const titel = normalisiereAbschnitt(text(ab.titel));
    if (titel && !hauptabschnitte.includes(titel)) hauptabschnitte.push(titel);
    (Array.isArray(ab.schritte) ? ab.schritte : []).forEach(s => {
      const st = objekt(s);
      const bezeichnung = text(st.bezeichnung);
      if (!bezeichnung) return;
      const von = kiDatum(st.von);
      const bis = kiDatum(st.bis);
      schritte.push({
        hauptabschnitt: titel,
        bezeichnung,
        schutzmassnahmen: textListe(st.schutzmassnahmen),
        vorschriften: textListe(st.vorschriften),
        hinweis_codes: leseHinweisCodes(textListe(st.hinweise)),
        zeitraum_von: von || null,
        zeitraum_bis: bis || null,
        koordinierungsbedarf: text(st.koordinierungsbedarf),
        zustaendig_erstellung: text(st.erstellung),
        zustaendig_kontrolle: text(st.kontrolle),
        zustaendig_instandhaltung: text(st.instandhaltung),
      });
    });
  });

  const nr = objekt(o.notrufplan);
  const notruf: AltplanNotruf = {
    krankenhaus: kiKontakt(nr.krankenhaus),
    durchgangsarzt: kiKontakt(nr.durchgangsarzt),
    augenarzt: kiKontakt(nr.augenarzt),
    apotheke: kiKontakt(nr.apotheke),
    ersthelfer: (Array.isArray(nr.ersthelfer) ? nr.ersthelfer : [])
      .map(e => ({ name: text(objekt(e).name), firma: text(objekt(e).firma) }))
      .filter(e => e.name)
      .map(e => (e.firma ? e : { name: e.name })),
  };
  const legende: LegendeEintrag[] = (Array.isArray(o.legende) ? o.legende : [])
    .map(l => ({ kuerzel: text(objekt(l).kuerzel), bedeutung: text(objekt(l).bedeutung) }))
    .filter(l => l.kuerzel && l.bedeutung);

  if (!schritte.length) {
    return { ...nichtErkannt('Auch die KI hat keine Zeile der Matrix lesen können.'), quelle: 'ki' };
  }
  const hinweise = ['Von der KI abgeschrieben — jede Zeile mit dem Original abgleichen, besonders Vorschriften und Zeiträume.'];
  const ohneZeit = schritte.filter(s => !s.zeitraum_von).length;
  if (ohneZeit) hinweise.push(`${ohneZeit} Arbeitsschritt(e) ohne Zeitraum.`);
  return { erkannt: true, quelle: 'ki', kopf, hauptabschnitte, schritte, notruf, legende, hinweise, ungeleseneSeiten: [] };
};
